"use client";
import {
    IAccountAssets,
    IBlocks,
    IBlog,
    IBlogPost,
    ITheme,
    IUserData,
} from "@/types";
import React from "react";
import { BlockUpdateFunction } from ".";
import AdminPopover from "../../content/AdminPopover";
import BlockToggle from "./tabs/toggle";
import Options from "./tabs/blogPostAll/options";
import Loading from "@/app/admin/(main)/loading";
import PaginateBlog from "../../blog/paginate";
import { BlogPostSkeletonPaginate } from "../../blog/skeleton";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { getTextColorForBackground, rgbToHex, shadow } from "@/lib/utils";
import { IconFilter, IconSearch } from "@tabler/icons-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import BlockMetadata from "./tabs/metadata";
interface Props {
    admin?: boolean;
    user: IUserData;
    block: IBlocks;
    update: BlockUpdateFunction;
    style: any;
    assets: IAccountAssets[];
    theme: ITheme;
    newBlock?: string | null;
    accountTheme: ITheme;
}
const itemsPerPage = 5;
export default function BlockBlogPostAll({
    admin,
    user,
    block,
    update,
    style,
    assets,
    theme,
    newBlock,
    accountTheme,
}: Props) {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    const [blog, setBlog] = React.useState<IBlog | null>(null);
    const [posts, setPosts] = React.useState<{
        posts: IBlogPost[];
        totalPosts: number;
        currentPage: number;
        totalPages: number;
    } | null>(null);
    const [category, setCategory] = React.useState<string | null>(null);
    const [categories, setCategories] = React.useState(blog?.categories || []);
    const [search, setSearch] = React.useState("");
    const [selectedCategories, setSelectedCategories] = React.useState<
        string[]
    >([]);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [isLoading, setIsLoading] = React.useState(true);
    React.useEffect(() => {
        if (newBlock === block.id) {
            handleExpand();
        }
        return () => {
            window.removeEventListener("closeBlock", handleClose);
        };
    }, [newBlock, block.id]);
    React.useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != block.id) {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    React.useEffect(() => {
        if (block != blockUpdate) {
            setBlockUpdate(block);
        }
    }, [block]);
    const handleClose = () => {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent("collapseBlock"));
    };
    function handleUpdate(value: IBlocks) {
        if (value == null) {
            update(blockUpdate.id, value, true);
            return;
        }
        setBlockUpdate({
            ...blockUpdate,
            ...value,
        });
        if (block == value) return;
        update(blockUpdate.id, value);
    }
    const event = new CustomEvent("expandBlock", {
        detail: {
            id: blockUpdate.id,
            title: "Full Blog",

            titleOptions: (
                <BlockToggle
                    block={blockUpdate}
                    blockUpdate={setBlockUpdate}
                    update={handleUpdate}
                />
            ),
            content: (
                <div className="flex flex-col gap-3 pb-3">
                    <Options
                        key={block.id}
                        account={user}
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                    />

                    <BlockMetadata block={blockUpdate} update={handleUpdate} />
                </div>
            ),
        },
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: block.id,
                },
            })
        );
        setIsExpanded(true);
        window.dispatchEvent(event);
    };
    React.useEffect(() => {
        if (isExpanded) {
            window.dispatchEvent(event);
        }
    }, [blockUpdate]);
    async function getPosts() {
        const res = await fetch(
            `/api/account/${user.id}/blog?page=1&limit=${itemsPerPage}${selectedCategories && selectedCategories?.length > 0 ? `&categories=[${selectedCategories}]` : ""}`
        );
        const data = await res.json();
        if (data.data) {
            setPosts(data.data);
            setBlog(data.data.blog);
            setCategories(data.data.blog.categories);
            setIsLoading(false);
        }
    }
    React.useEffect(() => {
        getPosts();
    }, [blockUpdate]);
    React.useEffect(() => {
        getPosts();
        setCurrentPage(1);
    }, [selectedCategories]);
    return (
        <AdminPopover
            admin={admin}
            className={block.show ? "" : "opacity-30"}
            onClick={handleExpand}
            title={"Blog Post All"}
            isOpen={isExpanded}
            onClose={handleClose}
            key={block.id}
        >
            <div
                key={block.id}
                className={`flex w-full flex-row flex-wrap gap-2 p-2 ${user.theme.button_rounded} relative`}
                id="BlogPostSingle"
            >
                {isLoading && <BlogPostSkeletonPaginate account={user} />}
                {blog && posts && (
                    <>
                        <style>
                            {`:root { 
                --blog-background-color: ${blog?.theme?.background};
                --blog-button-color: ${blog?.theme?.button_bg};
                --blog-button-text-color: ${blog?.theme?.button_text};
                --blog-heading-text: ${blog?.theme?.heading_text};
                }}`}
                        </style>

                        <div className="line-clamp-1 flex w-full items-center justify-between gap-2 pb-2">
                            <p
                                className="text-lg font-bold"
                                style={{
                                    color: user.theme.text_col_heading,
                                }}
                            >
                                {blockUpdate.name}
                            </p>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button
                                        id="block"
                                        className={` 
                ${theme?.button_rounded}
                ${shadow(theme.button_shadow)} 
                relative
                w-fit 
                p-1
                 text-xl 
                transition-all duration-150
                hover:scale-105
                `}
                                        style={{
                                            backgroundColor:
                                                blog.theme?.button_bg !==
                                                "rgba(0, 0, 0, 0)"
                                                    ? blog.theme?.button_bg
                                                    : theme.button || "black",
                                            color:
                                                blog.theme?.button_text !==
                                                "rgba(0, 0, 0, 0)"
                                                    ? blog.theme?.button_text
                                                    : theme?.text_col_button ||
                                                      "white",
                                        }}
                                    >
                                        {/* <img
                                            src="https://cdn.doras.to/doras/icons/light/doras.webp"
                                            className={
                                                "absolute left-4 top-1/2 -translate-y-1/2 transform"
                                            }
                                            alt="custom icons"
                                            style={{
                                                width: 26,
                                                height: 26,
                                            }}
                                        /> */}
                                        <IconFilter />
                                        {/* <span
                                            id="block-text"
                                            className={`block w-full px-6 pl-12 text-center font-bold`}
                                        >
                                            My Profile
                                        </span> */}
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent
                                    className={`
                                        max-h-64 w-full max-w-full overflow-auto bg-user-secondary-color p-2
                                    ${theme.button_rounded == "rounded-full" ? "rounded-xl" : theme.button_rounded} 
                                    ${shadow(theme.button_shadow)}`}
                                    // style={{
                                    //     background: blog.theme.background,
                                    // }}
                                >
                                    <div className="flex flex-col gap-3">
                                        <div
                                            className={`group flex items-center gap-2 border border-transparent p-1 transition-all duration-300 hover:border-user-primary-color ${theme.button_rounded == "rounded-full" ? "rounded-xl" : theme.button_rounded} `}
                                        >
                                            <Input
                                                variant={"card"}
                                                placeholder="Search categories..."
                                                value={search}
                                                className={`border-0 !bg-transparent text-user-primary-text-color placeholder:text-opacity-50 focus-visible:border-0 
                                                ${theme.button_rounded == "rounded-full" ? "rounded-xl" : theme.button_rounded} `}
                                                style={{
                                                    color:
                                                        getTextColorForBackground(
                                                            rgbToHex(
                                                                user.theme
                                                                    .accent_col
                                                            )
                                                        ) ||
                                                        getTextColorForBackground(
                                                            rgbToHex(
                                                                user.theme
                                                                    .button
                                                            )
                                                        ),
                                                }}
                                                onChange={(e) => {
                                                    setSearch(e.target.value);
                                                    setCategories(
                                                        blog.categories.filter(
                                                            (category) => {
                                                                return category.name
                                                                    .toLowerCase()
                                                                    .includes(
                                                                        e.target.value.toLowerCase()
                                                                    );
                                                            }
                                                        )
                                                    );
                                                }}
                                            />
                                            <Button
                                                className={`${theme.button_rounded == "rounded-full" ? "rounded-xl" : theme.button_rounded} bg-transparent hover:bg-user-primary-color`}
                                            >
                                                <IconSearch />
                                            </Button>
                                        </div>
                                        <Separator
                                            className="opacity-50"
                                            style={{
                                                background:
                                                    getTextColorForBackground(
                                                        rgbToHex(
                                                            user.theme
                                                                .accent_col
                                                        )
                                                    ) ||
                                                    getTextColorForBackground(
                                                        rgbToHex(
                                                            user.theme.button
                                                        )
                                                    ),
                                            }}
                                        />
                                        {categories.map((category) => (
                                            <div
                                                className="flex items-center space-x-2"
                                                key={category.id}
                                            >
                                                <Checkbox
                                                    className={`border-user-primary-color data-[state=checked]:border-user-primary-color data-[state=checked]:bg-user-primary-color`}
                                                    style={{
                                                        color: user.theme
                                                            .text_col_button,
                                                    }}
                                                    id={
                                                        "category-" +
                                                        category.id
                                                    }
                                                    onCheckedChange={(
                                                        checked
                                                    ) => {
                                                        if (checked) {
                                                            setSelectedCategories(
                                                                (prev) => [
                                                                    ...prev,
                                                                    `"${category.id}"`,
                                                                ]
                                                            );
                                                        } else {
                                                            setSelectedCategories(
                                                                (prev) =>
                                                                    prev.filter(
                                                                        (
                                                                            _category
                                                                        ) =>
                                                                            _category !==
                                                                            `"${category.id}"`
                                                                    )
                                                            );
                                                        }
                                                    }}
                                                    checked={selectedCategories.includes(
                                                        `"${category.id}"`
                                                    )}
                                                />
                                                <label
                                                    htmlFor={
                                                        "category-" +
                                                        category.id
                                                    }
                                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                                    style={{
                                                        color:
                                                            getTextColorForBackground(
                                                                rgbToHex(
                                                                    user.theme
                                                                        .accent_col
                                                                )
                                                            ) ||
                                                            getTextColorForBackground(
                                                                rgbToHex(
                                                                    user.theme
                                                                        .button
                                                                )
                                                            ),
                                                    }}
                                                >
                                                    {category.name}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </PopoverContent>
                            </Popover>
                        </div>
                        <PaginateBlog
                            account={user}
                            blog={blog}
                            posts={posts.posts || []}
                            itemsPerPage={itemsPerPage}
                            totalItems={posts.totalPosts || 0}
                            categories={selectedCategories}
                            pageNumber={currentPage}
                            accountTheme={accountTheme}
                        />
                    </>
                )}
            </div>
        </AdminPopover>
    );
}
